import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Tracks which row ids are actually scrolled into the viewport, via one
 * shared IntersectionObserver per hook instance. Ref callbacks are cached
 * per (id, slot) so React doesn't tear down/recreate the observer entry on
 * every re-render (e.g. every poll tick that merges a live count into the
 * row).
 *
 * `slot` matters when the same row id renders more than one DOM element at
 * once — e.g. a CSS-toggled mobile-card + desktop-row pair, where only one
 * is actually on screen at a given viewport width but both stay mounted. An
 * id counts as visible if ANY of its registered elements is intersecting;
 * pass a distinct `slot` per parallel render (e.g. `rowRef(id, 'mobile')`
 * and `rowRef(id, 'desktop')`) so tracking one doesn't evict the other.
 *
 * Usage: `ref={rowRef(row.creative_id)}` on each row's outer element
 * (`<tbody>`/card `<div>`). Returns a `visibleIds` Set for consumers like
 * `useUnreadCountPolling`.
 */
export const useVisibleRowIds = (rootMargin = '150px 0px') => {
  const [visibleIds, setVisibleIds] = useState(() => new Set());
  const observerRef = useRef(null);
  const elementToIdRef = useRef(new Map()); // element -> id
  const elementVisibleRef = useRef(new Map()); // element -> boolean
  const idToElementsRef = useRef(new Map()); // id -> Set(element)
  const slotElementRef = useRef(new Map()); // "id:slot" -> element
  const currentVisibleRef = useRef(new Set());
  const refCacheRef = useRef(new Map()); // "id:slot" -> ref callback

  const recomputeId = useCallback((id) => {
    const elements = idToElementsRef.current.get(id);
    const isVisible = elements ? [...elements].some((el) => elementVisibleRef.current.get(el)) : false;
    const wasVisible = currentVisibleRef.current.has(id);
    if (isVisible === wasVisible) return;
    if (isVisible) currentVisibleRef.current.add(id);
    else currentVisibleRef.current.delete(id);
    setVisibleIds(new Set(currentVisibleRef.current));
  }, []);

  const getObserver = () => {
    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          const touchedIds = new Set();
          entries.forEach((entry) => {
            const id = elementToIdRef.current.get(entry.target);
            if (id == null) return;
            elementVisibleRef.current.set(entry.target, entry.isIntersecting);
            touchedIds.add(id);
          });
          touchedIds.forEach(recomputeId);
        },
        { root: null, rootMargin, threshold: 0 }
      );
    }
    return observerRef.current;
  };

  const rowRef = useCallback((id, slot = 'default') => {
    if (id == null) return undefined;
    const key = `${id}:${slot}`;
    let fn = refCacheRef.current.get(key);
    if (!fn) {
      fn = (el) => {
        const observer = getObserver();
        const prevEl = slotElementRef.current.get(key);
        if (prevEl && prevEl !== el) {
          observer.unobserve(prevEl);
          elementToIdRef.current.delete(prevEl);
          elementVisibleRef.current.delete(prevEl);
          idToElementsRef.current.get(id)?.delete(prevEl);
        }
        if (el) {
          slotElementRef.current.set(key, el);
          elementToIdRef.current.set(el, id);
          elementVisibleRef.current.set(el, false);
          if (!idToElementsRef.current.has(id)) idToElementsRef.current.set(id, new Set());
          idToElementsRef.current.get(id).add(el);
          observer.observe(el);
        } else {
          slotElementRef.current.delete(key);
        }
        recomputeId(id);
      };
      refCacheRef.current.set(key, fn);
    }
    return fn;
  }, [recomputeId]);

  useEffect(() => () => {
    observerRef.current?.disconnect();
  }, []);

  return { visibleIds, rowRef };
};

export default useVisibleRowIds;
